import type { GameState } from '../state';
import { TICK_MS } from '../engine';

/** Offline accumulation is capped (ANATOMY §6 — the core retention dial). */
export const OFFLINE_CAP_MS = 12 * 60 * 60 * 1000; // 12h

/** Above this many ticks we'd burn too long replaying; clamp the work. */
const MAX_REPLAY_TICKS = 50_000;

export interface OfflineReport {
  /** Wall-clock ms that actually elapsed since the save. */
  rawElapsedMs: number;
  /** ms credited after applying the offline cap. */
  creditedMs: number;
  /** Whether the elapsed time hit the cap. */
  capped: boolean;
  /** Scrap before/after, for the "while you were away" summary. */
  scrapBefore: string;
  scrapAfter: string;
}

/**
 * Fast-forward the SAME tick path used online (TECHNICAL_APPROACH §5) so offline
 * behavior can't silently diverge. We replay fixed ticks; if the count is huge
 * we coarsen the tick size to stay within a bounded iteration budget rather than
 * a separate closed-form path (keeps one source of truth for the math).
 */
export function applyOffline(
  state: GameState,
  nowEpochMs: number,
  tickFn: (s: GameState, dt: number) => void,
): OfflineReport {
  const rawElapsedMs = Math.max(0, nowEpochMs - state.lastTickAt);
  const capped = rawElapsedMs > OFFLINE_CAP_MS;
  const creditedMs = Math.min(rawElapsedMs, OFFLINE_CAP_MS);

  const scrapBefore = state.wallet.scrap.toString();

  let remaining = creditedMs;
  const naiveTicks = Math.ceil(creditedMs / TICK_MS);
  // Coarsen if we'd exceed the replay budget.
  const step = naiveTicks > MAX_REPLAY_TICKS ? creditedMs / MAX_REPLAY_TICKS : TICK_MS;

  while (remaining > 0) {
    const dt = Math.min(step, remaining);
    tickFn(state, dt);
    remaining -= dt;
  }

  state.lastTickAt = nowEpochMs;

  return {
    rawElapsedMs,
    creditedMs,
    capped,
    scrapBefore,
    scrapAfter: state.wallet.scrap.toString(),
  };
}
